import { useState, useCallback, useMemo, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, increment, runTransaction } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import { buyCrypto, sellCrypto } from './CryptoFunctions';

const roundToTwoDecimals = (number) => {
  return parseFloat(number.toFixed(2));
};

const useBank = () => {
  const { user, updateUser } = useFireAuth();
  const [bank, setBank] = useState(null);
  const globalBankDocId = 'globalBank';
  const DEFAULT_BANK_MONEY = 1000000;
  const DEFAULT_BANK_HAHUMCOIN = 100;

  const fetchBankData = useCallback(async () => {
    const bankDocRef = doc(db, 'bank', globalBankDocId);
    const bankDocSnap = await getDoc(bankDocRef);
    if (!bankDocSnap.exists()) {
      const defaultBankData = {
        money: roundToTwoDecimals(DEFAULT_BANK_MONEY),
        hahumCoin: roundToTwoDecimals(DEFAULT_BANK_HAHUMCOIN),
      };
      await setDoc(bankDocRef, defaultBankData);
      return defaultBankData;
    }
    return bankDocSnap.data();
  }, []);

  const refreshUser = useCallback(async () => {
    await updateUser();
  }, [updateUser]);

  const updateBankMoney = useCallback(async (amount) => {
    const bankDocRef = doc(db, 'bank', globalBankDocId);
    await runTransaction(db, async (transaction) => {
      const bankDoc = await transaction.get(bankDocRef);
      if (!bankDoc.exists()) {
        throw new Error('Bank document does not exist!');
      }
      const newBankMoney = roundToTwoDecimals(bankDoc.data().money + amount);
      transaction.update(bankDocRef, { money: newBankMoney });
    });
    setBank(prevBank => ({ ...prevBank, money: roundToTwoDecimals(prevBank.money + amount) }));
  }, []);

  const updateBankHahumCoin = useCallback(async (amount) => {
    const bankDocRef = doc(db, 'bank', globalBankDocId);
    await runTransaction(db, async (transaction) => {
      const bankDoc = await transaction.get(bankDocRef);
      if (!bankDoc.exists()) {
        throw new Error('Bank document does not exist!');
      }
      const newBankHahumCoin = roundToTwoDecimals(bankDoc.data().hahumCoin + amount);
      transaction.update(bankDocRef, { hahumCoin: newBankHahumCoin });
    });
    setBank(prevBank => ({ ...prevBank, hahumCoin: roundToTwoDecimals(prevBank.hahumCoin + amount) }));
  }, []);

  const depositMoney = useCallback(async (amount, incrementGamesPlayed = true) => {
    if (!user) {
      console.error('User not logged in');
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const bankDocRef = doc(db, 'bank', globalBankDocId);

    try {
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        if (!userDoc.exists()) {
          throw new Error('User document does not exist!');
        }

        const currentUserMoney = userDoc.data().money || 0;
        if (currentUserMoney < amount) {
          throw new Error('Insufficient funds');
        }

        const newBalance = roundToTwoDecimals(currentUserMoney - amount);
        const updateData = {
          money: newBalance,
        };
        if (incrementGamesPlayed) {
          updateData.gamesPlayed = increment(1);
        }

        transaction.update(userDocRef, updateData);
        transaction.update(bankDocRef, {
          money: increment(roundToTwoDecimals(amount)),
        });
      });

      await refreshUser();
      console.log(`Deposit: User deposited ${amount}`);
    } catch (error) {
      console.error('Error during deposit transaction:', error);
    }
  }, [user, refreshUser]);

  const withdrawMoney = useCallback(async (amount) => {
    if (!user) {
      console.error('User not logged in');
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const bankDocRef = doc(db, 'bank', globalBankDocId);

    try {
      await runTransaction(db, async (transaction) => {
        const bankDoc = await transaction.get(bankDocRef);
        if (!bankDoc.exists()) {
          throw new Error('Bank document does not exist!');
        }

        const currentBankMoney = bankDoc.data().money || 0;
        if (currentBankMoney < amount) {
          throw new Error('Insufficient bank funds');
        }

        transaction.update(userDocRef, {
          money: increment(roundToTwoDecimals(amount))
        });
        transaction.update(bankDocRef, {
          money: increment(roundToTwoDecimals(-amount)),
        });
      });

      await refreshUser();
      await updateBankMoney(-amount);
      console.log(`Withdraw: User withdrew ${amount}`);
    } catch (error) {
      console.error('Error during withdraw transaction:', error);
    }
  }, [user, refreshUser, updateBankMoney]);

  const depositHahumCoin = useCallback(async (amount) => {
    if (user && bank && user.crypto?.hahumCoin >= amount) {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'crypto.hahumCoin': increment(roundToTwoDecimals(-amount))
      });
      await updateBankHahumCoin(roundToTwoDecimals(amount));
      await refreshUser();
      console.log(`Deposit: User deposited ${amount} hahumCoin`);
    } else {
      console.error('Insufficient hahumCoin or user/bank data not loaded');
    }
  }, [user, bank, updateBankHahumCoin, refreshUser]);

  const withdrawHahumCoin = useCallback(async (amount) => {
    if (user && bank && bank.hahumCoin >= amount) {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'crypto.hahumCoin': increment(roundToTwoDecimals(amount))
      });
      await updateBankHahumCoin(roundToTwoDecimals(-amount));
      await refreshUser();
      console.log(`Withdraw: User withdrew ${amount} HahumCoin`);
    } else {
      console.error('Insufficient HahumCoin or user/bank data not loaded');
    }
  }, [user, bank, updateBankHahumCoin, refreshUser]);

  const purchaseItem = useCallback(async (item) => {
    if (!user) {
        throw new Error('User not logged in');
    }

    const userRef = doc(db, 'users', user.uid);
    const bankRef = doc(db, 'bank', globalBankDocId);
    const isMoney = item.currency === 'money';
    const currentBalance = isMoney ? user.money : user.crypto?.hahumCoin;

    if (currentBalance < item.price) {
        throw new Error('Insufficient funds');
    }

    try {
        let purchasedItemId;
        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) {
                throw new Error('User document does not exist!');
            }

            const newBalance = roundToTwoDecimals(currentBalance - item.price);
            const currentInventory = userDoc.data().inventory || [];
            purchasedItemId = `${new Date().getTime()}_${item.name}`;

            const updateData = {
                inventory: [...currentInventory, purchasedItemId],
            };
            if (isMoney) {
                updateData.money = newBalance;
                transaction.update(bankRef, {
                    money: increment(roundToTwoDecimals(item.price)),
                });
            } else {
                updateData['crypto.hahumCoin'] = newBalance;
                transaction.update(bankRef, {
                    hahumCoin: increment(roundToTwoDecimals(item.price)),
                });
            }

            transaction.update(userRef, updateData);
        });

        await refreshUser();
        console.log(`Purchase: User purchased ${item.name} for ${item.price}`);
        return purchasedItemId;
    } catch (error) {
        console.error('Error purchasing item:', error);
        throw error;
    }
}, [user, refreshUser]);


  const removeItem = useCallback(async (itemId) => {
    if (!user || !user.inventory || !user.inventory.includes(itemId)) {
      console.log('Item not found in inventory');
      return false;
    }

    const userRef = doc(db, 'users', user.uid);
    try {
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          throw new Error('User document does not exist!');
        }
        const currentInventory = userDoc.data().inventory;
        if (!currentInventory.includes(itemId)) {
          throw new Error('Item no longer in inventory');
        }
        const updatedInventory = currentInventory.filter(item => item !== itemId);
        transaction.update(userRef, {
          inventory: updatedInventory,
        });
      });
      await refreshUser();
      console.log(`Item ${itemId} removed successfully`);
      return true;
    } catch (error) {
      console.error('Error removing item:', error);
      return false;
    }
  }, [user, refreshUser]);

  useEffect(() => {
    const loadBankData = async () => {
      const bankData = await fetchBankData();
      setBank(bankData);
    };
    loadBankData();
  }, [fetchBankData]);

  return useMemo(() => ({
    user,
    bank,
    depositMoney,
    withdrawMoney,
    depositHahumCoin,
    withdrawHahumCoin,
    refreshUser,
    purchaseItem,
    removeItem,
    buyCrypto,
    sellCrypto
  }), [user, bank, depositMoney, withdrawMoney, depositHahumCoin, withdrawHahumCoin, refreshUser, purchaseItem, removeItem]);
};

export default useBank;
